/**
 * Wallet History Page
 *
 * 카피 트레이딩 지갑 내역 페이지
 * - 통합 지갑 API를 통한 카피 지갑 거래 내역 조회
 * - 입출금, 이체, 손익 등 상세 히스토리
 * - 필터링 (거래 유형, 날짜 범위)
 * - 페이지네이션
 */

import React, { useState, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Table, Select, DatePicker, Tag, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '@/theme/ThemeProvider';
import { useNewApiFetcher } from '@app/hooks/fetcher';
import { formatDateTime } from '@/utils/formatDateTime';
import { useTradingWallets } from '../../hooks/user/useTradingWallets';
import { formatDecimal } from '@utils/formatDecimal';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface HistoryItem {
  id: number;
  userId: number;
  symbol: string;
  fromWallet: number | null;
  toWallet: number | null;
  amount: string;
  type: string;
  transactionType: string;
  detail: string | null;
  info: string | null;
  sentAt: string;
  adminMemo: string | null;
  beforeAmount: string | null;
  afterAmount: string | null;
  walletType: number | null;
}

export function WalletHistoryPage() {
  const { t } = useTranslation();
  const { isDarkMode } = useContext(ThemeContext);

  // 카피 지갑 정보
  const { data: walletData, getCopyAvailable } = useTradingWallets();

  // 필터 상태
  const [transactionType, setTransactionType] = useState<string>('all');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // API URL 생성
  const apiUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.append('tradingType', 'copy'); // 카피 트레이딩 지갑만 조회
    params.append('page', page.toString());
    params.append('maxData', pageSize.toString());

    if (transactionType !== 'all') {
      params.append('transactionTypes', transactionType);
    }

    if (dateRange[0] && dateRange[1]) {
      params.append('startDate', dateRange[0].format('YYYY-MM-DD'));
      params.append('endDate', dateRange[1].format('YYYY-MM-DD'));
    }

    return `/wallet/history?${params.toString()}`;
  }, [transactionType, dateRange, page]);

  const { data, isLoading, refresh } = useNewApiFetcher(apiUrl, true, {
    revalidateOnFocus: true,
  });

  // 거래 유형 설정
  const getTransactionTypeConfig = (type: string) => {
    const configs: Record<string, { color: string; label: string }> = {
      DEPOSIT: { color: '#2ebd85', label: t('deposit') },
      WITHDRAWAL: { color: '#f6465d', label: t('withdrawal') },
      ADMIN_DEPOSIT: { color: '#16a085', label: t('adminDeposit') },
      TRANSFER_IN: { color: '#4a90e2', label: t('transferIn') },
      TRANSFER_OUT: { color: '#9b59b6', label: t('transferOut') },
      TRADE_FEE: { color: '#e67e22', label: t('tradeFee') },
      REALIZED_PNL: { color: '#27ae60', label: t('realizedPnl') },
      COMMISSION: { color: '#3498db', label: t('referralCommission') },
      REWARD: { color: '#f1c40f', label: t('reward') },
      FUNDING_FEE_PAID: { color: '#e74c3c', label: t('fundingFeeMinus') },
      FUNDING_FEE_RECEIVED: { color: '#2ecc71', label: t('fundingFeePlus') },
      LIQUIDATION: { color: '#c0392b', label: t('forcedLiquidation') },
      LOCK: { color: '#95a5a6', label: t('lock') },
      UNLOCK: { color: '#7f8c8d', label: t('unlock') },
      ADJUSTMENT: { color: '#8e44ad', label: t('adjustment') },
      PROFIT_SHARE: { color: '#e91e63', label: t('profitShare') || 'Profit Share' },
    };

    return configs[type] || { color: '#888', label: type };
  };

  // 테이블 컬럼 정의
  const columns: ColumnsType<HistoryItem> = [
    {
      title: t('date'),
      dataIndex: 'sentAt',
      key: 'sentAt',
      align: 'center',
      width: 160,
      render: (text: string) => formatDateTime(text),
    },
    {
      title: t('symbol'),
      dataIndex: 'symbol',
      key: 'symbol',
      align: 'center',
      width: 80,
    },
    {
      title: t('transactionType'),
      dataIndex: 'transactionType',
      key: 'transactionType',
      align: 'center',
      width: 140,
      render: (type: string) => {
        const config = getTransactionTypeConfig(type);
        return (
          <Tag
            style={{
              backgroundColor: config.color,
              color: 'white',
              padding: '4px 12px',
              borderRadius: '4px',
              border: 'none',
              fontWeight: 500,
            }}
          >
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: t('amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      width: 140,
      render: (amount: string, record: HistoryItem) => {
        let isPositive = record.type === 'D';
        if (record.transactionType === 'TRANSFER_OUT') {
          isPositive = false;
        } else if (record.transactionType === 'TRANSFER_IN') {
          isPositive = true;
        }

        return (
          <AmountText positive={isPositive}>
            {isPositive ? '+' : '-'}
            {formatDecimal(Number(amount), 4)} USDT
          </AmountText>
        );
      },
    },
    {
      title: t('beforeBalance'),
      dataIndex: 'beforeAmount',
      key: 'beforeAmount',
      align: 'right',
      width: 120,
      render: (amount: string | null) =>
        amount ? `${formatDecimal(Number(amount), 4)} USDT` : '-',
    },
    {
      title: t('afterBalance'),
      dataIndex: 'afterAmount',
      key: 'afterAmount',
      align: 'right',
      width: 120,
      render: (amount: string | null) =>
        amount ? `${formatDecimal(Number(amount), 4)} USDT` : '-',
    },
    {
      title: t('info'),
      dataIndex: 'info',
      key: 'info',
      align: 'center',
      width: 200,
      render: (info: string | null) => <InfoText>{info || '-'}</InfoText>,
    },
  ];

  // 핸들러
  const handleRefresh = () => {
    refresh();
  };

  const handleResetFilters = () => {
    setTransactionType('all');
    setDateRange([null, null]);
    setPage(1);
  };

  return (
    <PageWrapper>
      {/* 헤더 */}
      <CompactHeader isDark={isDarkMode}>
        <HeaderLeft>
          <HeaderTitle>{t('walletHistory')}</HeaderTitle>
          <HeaderDescription>{t('walletHistoryDesc')}</HeaderDescription>
        </HeaderLeft>

        <StatsRow>
          <StatItem>
            <StatLabel>{t('copyWalletBalance')}</StatLabel>
            <StatValue>
              {formatDecimal(parseFloat(getCopyAvailable()), 2)} USDT
            </StatValue>
            {walletData?.copy && (
              <StatSubtext>
                {t('locked')}:{' '}
                {formatDecimal(parseFloat(walletData.copy.lockbalance), 2)} USDT
              </StatSubtext>
            )}
          </StatItem>
          <StatDivider />
          <StatItem>
            <StatLabel>{t('totalRecords')}</StatLabel>
            <StatValue>{data?.result?.totalCount || 0}</StatValue>
          </StatItem>
          <StatDivider />
          <StatItem>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              {t('refresh')}
            </Button>
          </StatItem>
        </StatsRow>
      </CompactHeader>

      {/* 필터 섹션 */}
      <FilterSection isDark={isDarkMode}>
        <FilterRow>
          <FilterItem>
            <FilterLabel>{t('transactionType')}</FilterLabel>
            <Select
              style={{ width: 180 }}
              placeholder={t('all')}
              allowClear
              value={transactionType}
              onChange={(value) => {
                setTransactionType(value || 'all');
                setPage(1);
              }}
            >
              <Option value="all">{t('all')}</Option>
              <Option value="DEPOSIT">{t('deposit')}</Option>
              <Option value="WITHDRAWAL">{t('withdrawal')}</Option>
              <Option value="TRANSFER_IN">{t('transferIn')}</Option>
              <Option value="TRANSFER_OUT">{t('transferOut')}</Option>
              <Option value="REALIZED_PNL">{t('realizedPnl')}</Option>
              <Option value="TRADE_FEE">{t('tradeFee')}</Option>
              <Option value="FUNDING_FEE_PAID">{t('fundingFeeMinus')}</Option>
              <Option value="FUNDING_FEE_RECEIVED">{t('fundingFeePlus')}</Option>
              <Option value="LIQUIDATION">{t('forcedLiquidation')}</Option>
            </Select>
          </FilterItem>

          <FilterItem>
            <FilterLabel>{t('period')}</FilterLabel>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                setDateRange(dates as [Dayjs | null, Dayjs | null]);
                setPage(1);
              }}
              format="YYYY-MM-DD"
              style={{ width: 280 }}
            />
          </FilterItem>

          <Button
            icon={<FilterOutlined />}
            onClick={handleResetFilters}
            style={{ marginTop: 22 }}
          >
            {t('filterReset')}
          </Button>
        </FilterRow>
      </FilterSection>

      {/* 테이블 */}
      <TableSection isDark={isDarkMode}>
        <Table<HistoryItem>
          columns={columns}
          dataSource={data?.result?.list || []}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: data?.result?.totalCount || 0,
            onChange: (newPage) => setPage(newPage),
            showSizeChanger: false,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} / ${total} ${t('items')}`,
          }}
          rowKey={(record) => record.id}
          scroll={{ x: 'max-content' }}
          loading={isLoading}
          locale={{
            emptyText: t('noTransactionHistory'),
          }}
        />
      </TableSection>

      {/* 빈 상태 */}
      {!isLoading && (data?.result?.list || []).length === 0 && (
        <EmptyState isDark={isDarkMode}>
          <EmptyIcon>💰</EmptyIcon>
          <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            {t('noWalletHistory')}
          </p>
          <p style={{ fontSize: 14, opacity: 0.7 }}>
            {t('noWalletHistoryDesc')}
          </p>
        </EmptyState>
      )}
    </PageWrapper>
  );
}

// ==========================================
// Styled Components
// ==========================================

const PageWrapper = styled.div`
  display: flex;
  max-width: 1200px;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;

  @media (max-width: 1240px) {
    padding: 20px;
  }
`;

const CompactHeader = styled.div<{ isDark: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(200px, 1fr) auto;
  align-items: center;
  gap: 24px;
  margin-bottom: 20px;
  padding: 20px 24px;
  box-sizing: border-box;
  background: ${({ isDark }) =>
    isDark
      ? 'linear-gradient(135deg, rgba(255, 145, 49, 0.05) 0%, rgba(20, 17, 34, 0.05) 100%), #1a1a1a'
      : 'linear-gradient(135deg, rgba(255, 145, 49, 0.03) 0%, rgba(255, 255, 255, 1) 100%)'};
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.text};
`;

const HeaderDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: auto auto auto auto auto;
  align-items: center;
  gap: 20px;
  justify-self: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    width: 100%;
    justify-self: stretch;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  min-width: 110px;

  @media (max-width: 768px) {
    align-items: center;
    min-width: unset;
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  white-space: nowrap;
`;

const StatSubtext = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
  margin-top: 2px;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 32px;
  background-color: ${({ theme }) => theme.text};
  opacity: 0.1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const FilterSection = styled.div<{ isDark: boolean }>`
  width: 100%;
  margin-bottom: 24px;
  padding: 20px;
  box-sizing: border-box;
  background-color: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
`;

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

const TableSection = styled.div<{ isDark: boolean }>`
  width: 100%;
  background-color: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border-radius: 12px;
  padding: 20px;
  box-sizing: border-box;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
`;

const AmountText = styled.span<{ positive: boolean }>`
  font-weight: 600;
  color: ${(props) =>
    props.positive ? 'rgb(46, 189, 133)' : 'rgb(246, 70, 93)'};
`;

const InfoText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.secondaryTextColor || '#666'};
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
`;

const EmptyState = styled.div<{ isDark: boolean }>`
  width: 100%;
  margin-top: 24px;
  padding: 80px 20px;
  box-sizing: border-box;
  text-align: center;
  background: ${({ isDark }) =>
    isDark
      ? 'linear-gradient(135deg, rgba(255, 145, 49, 0.03) 0%, rgba(20, 17, 34, 0.03) 100%), #1a1a1a'
      : 'linear-gradient(135deg, rgba(255, 145, 49, 0.02) 0%, rgba(255, 255, 255, 1) 100%)'};
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #f0f0f0')};
  color: ${({ theme }) => theme.text};

  p {
    margin: 8px 0;
  }
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;
